import { useLazyQuery } from '@apollo/client';
import { organizationMembersFileName } from '@common/neo.tax';
import { captureException } from '@sentry/react';
import { useCallback } from 'react';

import {
  GET_ORGANIZATION_MEMBERS_CSV,
  GetOrganizationMembersCsvQuery,
  GetOrganizationMembersCsvQueryVariables,
} from '@neo.tax/shared/graphql';
import { ToastConfigState } from '@neo.tax/shared/state';
import { downloadFileByUrl, makeCsvFileAndUrl } from '@neo.tax/shared/utils';

export const useExportOrganizationMembers = ({
  organizationId,
  organizationName,
}: {
  organizationId: string;
  organizationName: string;
}): { exportMembers: () => Promise<void>; isExporting: boolean } => {
  const [fetchMembersCsv, { loading }] = useLazyQuery<
    GetOrganizationMembersCsvQuery,
    GetOrganizationMembersCsvQueryVariables
  >(GET_ORGANIZATION_MEMBERS_CSV, {
    // The roster is read once to build the file and never rendered, so it should be fetched fresh
    // and not retained: an access review must reflect the moment it was taken, and the members'
    // names, emails and login history have no reason to sit in the cache afterwards.
    fetchPolicy: 'no-cache',
  });

  const exportMembers = useCallback(async (): Promise<void> => {
    try {
      const { data } = await fetchMembersCsv({ variables: { organizationId } });
      if (!data) {
        throw new Error('No members export returned');
      }

      const { columnNames, resultCsv, signedUrl } = data.getOrganizationMembersCsv;

      // Large exports are uploaded and handed back as a link, which names the file itself.
      if (signedUrl) {
        downloadFileByUrl(signedUrl);
        return;
      }

      const { revokeUrl, url } = makeCsvFileAndUrl(`${columnNames.join(',')}\n${resultCsv}`);
      downloadFileByUrl(url, organizationMembersFileName(organizationName, new Date()));
      // Revoking in this task cancels the download in Firefox and Safari, which surfaces as a
      // button that spins and produces no file.
      setTimeout(revokeUrl, 0);
    } catch (error) {
      // Reported as well as toasted: every failure here reads the same to the user, so without
      // this a bug in the download path is indistinguishable from the server being unreachable.
      captureException(error, { extra: { source: 'useExportOrganizationMembers', organizationId } });
      ToastConfigState.pushErrorToast("We couldn't export your members. Please try again.");
    }
  }, [fetchMembersCsv, organizationId, organizationName]);

  return { exportMembers, isExporting: loading };
};
